/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { disablePageScroll, enablePageScroll } from 'scroll-lock';
import { FocusScope } from 'react-aria';
import useKeys from 'effects/keys';

import styles from './Modal.module.scss';

/**
 * This is the Modal component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function Modal({
  visible,
  onClose,
  children,
  forceRender,
  classNameClose,
  autoFocus,
}) {
  const modalRoot = useMemo(() => {
    return document.querySelector('#modal-root');
  }, []);

  const className = classNames(styles.main, {
    [styles.visible]: visible,
  });

  useKeys('modal', visible, {
    esc: onClose,
  });

  useEffect(() => {
    if (visible) {
      disablePageScroll();
    } else {
      enablePageScroll();
    }
  }, [visible]);

  if (!modalRoot) {
    console.warn('No modal root to mount video overlay in');
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={className}
      onClick={(e) => {
        if (e.target.classList.contains(styles.main)) {
          onClose();
        }
      }}
    >
      {(visible || forceRender) && (
        <FocusScope contain restoreFocus autoFocus={autoFocus}>
          <>
            {children}
            <button
              onClick={onClose}
              className={classNames(styles.close, classNameClose)}
              aria-label="Close"
              tabIndex={visible ? 0 : -1}
            />
          </>
        </FocusScope>
      )}
    </div>,

    modalRoot
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Modal.propTypes = {
  visible: PropTypes.bool,
  forceRender: PropTypes.bool,
  classNameClose: PropTypes.string,
  autoFocus: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Modal.defaultProps = {
  visible: false,
  forceRender: false,
  classNameClose: null,
  autoFocus: true,
};

export default Modal;
